'use client';

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from '@tanstack/react-query';
import { createContext, type FC, type PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useUserStore } from '@/entities/User/store/userStoreProvider';
import type { User } from '@/entities/User/types/user';
import { FirstTimePeriodEntryModal, type FirstTimePeriodEntryModalOptions, type FirstTimePeriodEntryModalReward } from '@/shared/components/FirstTimePeriodEntryModal';
interface OpenFirstTimePeriodEntryModalParams {
  reward: FirstTimePeriodEntryModalReward;
  options?: FirstTimePeriodEntryModalOptions;
}
interface FirstTimePeriodEntryModalContextType {
  isOpen: boolean;
  openModal: (params: OpenFirstTimePeriodEntryModalParams) => void;
  closeModal: () => void;
}
const FirstTimePeriodEntryModalContext = createContext<FirstTimePeriodEntryModalContextType | null>(null);

/** Часы для подзаголовка модалки: до конца соревнования из профиля или запасное значение. */
const getHoursForFirstTimePeriodModal = (user: User): number => {
  const endRaw = user.competition?.endDateTime;
  if (!endRaw) {
    return 8;
  }
  const endMs = new Date(endRaw).getTime();
  if (Number.isNaN(endMs)) {
    return 8;
  }
  const diffMs = endMs - Date.now();
  if (diffMs <= 0) {
    return 1;
  }
  const hours: number = Math.ceil(diffMs / (60 * 60 * 1000));
  return Math.max(1, hours);
};
export const useFirstTimePeriodEntryModal = () => {
  const context = useContext(FirstTimePeriodEntryModalContext);
  if (!context) {
    throw new Error("useFirstTimePeriodEntryModal must be used within FirstTimePeriodEntryModalProvider");
  }
  return context;
};

/**
 * @description Глобальная модалка «1st Time Period Entry»: при `actionCompleted.isFirstTime` и
 * `actionCompleted.modalAlreadyShown !== true` (флаг с профиля, синхрон между устройствами).
 */
export const FirstTimePeriodEntryModalProvider: FC<PropsWithChildren> = t0 => {
  const $ = _c(21);
  const {
    children
  } = t0;
  const queryClient = useQueryClient();
  const [isOpen, setIsOpen] = useState(false);
  const [reward, setReward] = useState(null);
  const [options, setOptions] = useState(undefined);
  const optionsRef = useRef(undefined);
  const presentedFirstTimeActionKeyRef = useRef(null);
  const user = useUserStore(_temp);
  let t1;
  let t2;
  if ($[0] !== options) {
    t1 = () => {
      optionsRef.current = options;
    };
    t2 = [options];
    $[0] = options;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== queryClient) {
    t3 = () => {
      optionsRef.current?.onClose?.();
      setIsOpen(false);
      setReward(null);
      setOptions(undefined);
      queryClient.invalidateQueries({
        queryKey: ["user"]
      });
    };
    $[3] = queryClient;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const closeModal = t3;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = t5 => {
      const {
        reward: reward_0,
        options: options_0
      } = t5;
      setReward(reward_0);
      setOptions(options_0);
      optionsRef.current = options_0;
      setIsOpen(true);
    };
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const openModal = t4;
  let t5;
  let t6;
  if ($[6] !== user) {
    t5 = () => {
      if (!user) {
        presentedFirstTimeActionKeyRef.current = null;
        return;
      }
      const ac = user.actionCompleted;
      if (!ac?.isFirstTime) {
        return;
      }
      if (user.modalAlreadyShown === true) {
        presentedFirstTimeActionKeyRef.current = null;
        return;
      }
      const key = `${user.id}-${ac.actionTitle}-${ac.coinsReward}-${ac.poeReward}`;
      if (presentedFirstTimeActionKeyRef.current === key) {
        return;
      }
      presentedFirstTimeActionKeyRef.current = key;
      openModal({
        reward: {
          coins: ac.coinsReward,
          poe: ac.poeReward,
          hours: getHoursForFirstTimePeriodModal(user)
        }
      });
    };
    t6 = [user, openModal];
    $[6] = user;
    $[7] = t5;
    $[8] = t6;
  } else {
    t5 = $[7];
    t6 = $[8];
  }
  useEffect(t5, t6);
  let t7;
  if ($[9] !== closeModal || $[10] !== isOpen) {
    t7 = {
      isOpen,
      openModal,
      closeModal
    };
    $[9] = closeModal;
    $[10] = isOpen;
    $[11] = t7;
  } else {
    t7 = $[11];
  }
  const value = t7;
  let t8;
  if ($[12] !== closeModal || $[13] !== isOpen || $[14] !== options || $[15] !== reward) {
    t8 = <FirstTimePeriodEntryModal open={isOpen} onClose={closeModal} options={options} reward={reward} />;
    $[12] = closeModal;
    $[13] = isOpen;
    $[14] = options;
    $[15] = reward;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== children || $[18] !== t8 || $[19] !== value) {
    t9 = <FirstTimePeriodEntryModalContext.Provider value={value}>{children}{t8}</FirstTimePeriodEntryModalContext.Provider>;
    $[17] = children;
    $[18] = t8;
    $[19] = value;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  return t9;
};
function _temp(store) {
  return store.user;
}